import { useState } from 'react';
import { X, Flag, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/toast';

const REPORT_REASONS = [
  { value: 'prohibited_item', label: 'Prohibited item' },
  { value: 'misleading_description', label: 'Misleading/inaccurate description' },
  { value: 'suspected_scam', label: 'Suspected scam' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'duplicate_listing', label: 'Duplicate listing' },
  { value: 'other', label: 'Other' }
];

export function ReportModal({ isOpen, onClose, itemId, itemType, itemTitle }) {
  const { api } = useAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reports/', {
        item_id: itemId,
        item_type: itemType,
        reason: selectedReason,
        details: details || null
      });
      
      toast.success('Report submitted. Thank you for helping keep WANTD safe.');
      onClose();
      
      // Reset form
      setSelectedReason('');
      setDetails('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit report';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="report-modal"
    >
      <div className="bg-[var(--theme-card)] rounded-2xl w-full max-w-md border border-[var(--theme-border)] shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--theme-border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <Flag className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Report this listing</h2>
              <p className="text-xs text-[var(--theme-text-muted)] truncate max-w-[200px]">{itemTitle}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors"
            data-testid="close-report-modal"
          >
            <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Reason Selection */}
          <div>
            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-3">
              Why are you reporting this listing?
            </label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <label 
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedReason === reason.value
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                      : 'border-[var(--theme-border)] hover:border-[var(--theme-text-muted)] hover:bg-[var(--theme-hover)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-4 h-4 text-red-500 focus:ring-red-500 border-[var(--theme-border)]"
                    data-testid={`reason-${reason.value}`}
                  />
                  <span className={`text-sm ${
                    selectedReason === reason.value
                      ? 'text-[var(--theme-text-primary)] font-medium'
                      : 'text-[var(--theme-text-secondary)]'
                  }`}>
                    {reason.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div>
            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-2">
              Additional details <span className="text-[var(--theme-text-muted)] font-normal">(optional)</span>
            </label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional context that might help us review this report..."
              className="bg-[var(--theme-bg)] border-[var(--theme-border)] resize-none"
              rows={3}
              maxLength={500}
              data-testid="report-details-input"
            />
            <p className="text-xs text-[var(--theme-text-muted)] mt-1 text-right">
              {details.length}/500
            </p>
          </div>

          {/* Info Banner */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              False reports may result in restrictions on your account. Only report genuine concerns.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-4 border-t border-[var(--theme-border)]">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="flex-1 border-[var(--theme-border)]"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            data-testid="submit-report-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Flag className="w-4 h-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Also export as default for backwards compatibility
export default ReportModal;
